import React, { useState, useEffect, useRef } from 'react';
import { useFocusTrap } from '../lib/useFocusTrap';
import {
  ArrowUp, Mic, MicOff, Volume2, VolumeX, X, SquarePen,
  Copy, Check, ExternalLink, Mail, Phone, Github, Linkedin
} from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { contactInfo } from '../data/assistant/knowledge';
import { createAssistant, quickQuestions } from '../data/assistant/engine';
import { projectIndex } from '../data/assistant/projectIndex';

const assistant = createAssistant({ projects: projectIndex });

/* Monochrome palette for the assistant shell — it is always dark, like a device screen,
   regardless of the site theme. */
const ink = {
  canvas: '#0a0a0a',
  panel: '#121212',
  bubble: '#1f1f1f',
  hover: '#262626',
  line: 'rgba(255,255,255,0.08)',
  fg: '#ededed',
  fg2: '#a3a3a3',
  fg3: '#6b6b6b',
};

/** Wireframe orb mark — three interlaced loops inside a ring. */
function OrbMark({ size = 16, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M3.5 9.5c5.5-1.5 11.5 1 17 7" />
      <path d="M3.5 14.5c5.5 1.5 11.5-1 17-7" />
      <path d="M12 2c-3 6-3 14 0 20" />
    </svg>
  );
}

/** Large animated wireframe sphere used on the welcome screen. */
function WireOrb({ listening = false, size = 'w-44 h-44' }) {
  const rings = [0, 30, 60, 90, 120, 150];
  return (
    <div className={`cb-orb relative mx-auto ${size} ${listening ? 'cb-orb-listening' : ''}`} aria-hidden="true">
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_45%,rgba(255,255,255,0.14),rgba(255,255,255,0.02)_55%,transparent_70%)]" />
      <svg viewBox="0 0 200 200" className="cb-orb-spin absolute inset-0 w-full h-full" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.6">
        {rings.map((deg) => (
          <ellipse key={`a${deg}`} cx="100" cy="100" rx="78" ry="26" transform={`rotate(${deg} 100 100)`} />
        ))}
      </svg>
      <svg viewBox="0 0 200 200" className="cb-orb-spin-reverse absolute inset-0 w-full h-full" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5">
        {rings.map((deg) => (
          <ellipse key={`b${deg}`} cx="100" cy="100" rx="60" ry="78" transform={`rotate(${deg + 15} 100 100)`} />
        ))}
        <circle cx="100" cy="100" r="80" stroke="rgba(255,255,255,0.18)" />
      </svg>
    </div>
  );
}

/**
 * Format markdown **bold** text cleanly into HTML React elements
 */
function Inline({ text }) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
      : part
  );
}

function FormatMarkdownText({ text = '' }) {
  const lines = text.split('\n');
  const out = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="space-y-1 my-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="shrink-0" style={{ color: ink.fg3 }}>•</span>
              <span><Inline text={b} /></span>
            </li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  lines.forEach((line, i) => {
    if (line.startsWith('• ')) { bullets.push(line.slice(2)); return; }
    flush();
    if (line.trim() === '—') { out.push(<hr key={i} className="my-2 border-0 border-t" style={{ borderColor: ink.line }} />); return; }
    if (line.trim() === '') { out.push(<span key={i} className="block h-2" />); return; }
    out.push(<p key={i}><Inline text={line} /></p>);
  });
  flush();
  return <div className="space-y-0.5">{out}</div>;
}

/**
 * Single Bot Message Bubble with extracted Action Buttons & Copy Features
 */
function BotMessageBubble({ message, isLast = false, onFollowUp = () => {} }) {
  const [copiedKey, setCopiedKey] = useState(null);

  const text = message.text || '';

  // Copy helper
  const handleCopy = (content, key) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(content);
    } else {
      const el = document.createElement('textarea');
      el.value = content;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Detect URLs in message text
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urls = text.match(urlRegex) || [];

  // Detect Email & Phone in message text
  const hasEmail = text.includes(contactInfo.email);
  const hasPhone = text.includes(contactInfo.phone);
  const hasLinkedIn = text.includes('linkedin.com');
  const hasGitHub = text.includes('github.com');

  // Determine button title for URLs
  const getUrlLabel = (url) => {
    if (url.includes('baton-landing')) return 'Baton Live Site';
    if (url.includes('Baton-Multi-Agent')) return 'Baton GitHub Repo';
    if (url.includes('github.com')) return 'GitHub Profile';
    if (url.includes('linkedin.com')) return 'LinkedIn Profile';
    if (url.includes('netlify.app')) return 'Live Portfolio';
    if (url.includes('51.20.4.202')) return 'Alumni Portal Demo';
    return 'Open Link';
  };

  const pill = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors cursor-pointer";
  const pillStyle = { background: ink.bubble, borderColor: ink.line, color: ink.fg2 };

  const actions = [];
  (message.links || []).forEach((l) => actions.push(
    <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className={pill} style={pillStyle} {...(l.url.endsWith('.pdf') ? { download: 'Rakshan_Shetty_Resume.pdf' } : {})}>
      <ExternalLink size={11} /><span>{l.label}</span>
    </a>
  ));
  const hasStructured = (message.links || []).length > 0;
  if (!hasStructured) urls.forEach((url) => actions.push(
    <a key={url} href={url} target="_blank" rel="noopener noreferrer" className={pill} style={pillStyle}>
      <ExternalLink size={11} /><span>{getUrlLabel(url)}</span>
    </a>
  ));
  if (!hasStructured && hasGitHub && !urls.some(u => u.includes('github.com'))) actions.push(
    <a key="gh" href={`https://${contactInfo.github}`} target="_blank" rel="noopener noreferrer" className={pill} style={pillStyle}>
      <Github size={11} /><span>GitHub</span>
    </a>
  );
  if (!hasStructured && hasLinkedIn && !urls.some(u => u.includes('linkedin.com'))) actions.push(
    <a key="li" href={`https://${contactInfo.linkedin}`} target="_blank" rel="noopener noreferrer" className={pill} style={pillStyle}>
      <Linkedin size={11} /><span>LinkedIn</span>
    </a>
  );
  if (!hasStructured && hasEmail) actions.push(
    <button key="email" type="button" onClick={() => handleCopy(contactInfo.email, 'email')} className={pill} style={pillStyle}>
      {copiedKey === 'email' ? <Check size={11} /> : <Mail size={11} />}<span>{copiedKey === 'email' ? 'Copied' : 'Copy email'}</span>
    </button>
  );
  if (!hasStructured && hasPhone) actions.push(
    <button key="phone" type="button" onClick={() => handleCopy(contactInfo.phone, 'phone')} className={pill} style={pillStyle}>
      {copiedKey === 'phone' ? <Check size={11} /> : <Phone size={11} />}<span>{copiedKey === 'phone' ? 'Copied' : 'Copy phone'}</span>
    </button>
  );

  return (
    <div className="flex items-start gap-2.5 pr-6">
      <span
        className="mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center border"
        style={{ borderColor: 'rgba(255,255,255,0.25)', color: ink.fg }}
      >
        <OrbMark size={13} />
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="text-[13px] leading-relaxed" style={{ color: ink.fg }}>
          <FormatMarkdownText text={text} />
          {message.streaming && <span className="cb-cursor" aria-hidden="true" />}
        </div>
        {!message.streaming && (
        <div className="flex flex-wrap items-center gap-1.5">
          {actions}
          <button
            type="button"
            onClick={() => handleCopy(text.replace(/\*\*/g, ''), 'text')}
            className="inline-flex items-center gap-1 px-1.5 py-1 rounded-full text-[11px] transition-colors cursor-pointer hover:text-white"
            style={{ color: ink.fg3 }}
            title="Copy response"
          >
            {copiedKey === 'text' ? <Check size={11} /> : <Copy size={11} />}
          </button>
        </div>
        )}
        {!message.streaming && isLast && message.followUps?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {message.followUps.slice(0, 3).map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => onFollowUp(q)}
                className="text-[11px] px-2.5 py-1 rounded-full border transition-colors cursor-pointer hover:bg-white/10"
                style={{ borderColor: 'rgba(255,255,255,0.18)', color: ink.fg2 }}
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Full-body overlay shown while the microphone is capturing speech. */
function ListeningOverlay({ onStop }) {
  const bars = [0, 1, 2, 3, 4, 5, 6, 7, 8];
  return (
    <div
      className="cb-listening absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center"
      style={{ background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(6px)' }}
      role="status"
      aria-live="polite"
    >
      <div className="relative">
        <span className="cb-ripple absolute inset-0 rounded-full" />
        <span className="cb-ripple cb-ripple-2 absolute inset-0 rounded-full" />
        <WireOrb listening size="w-40 h-40" />
      </div>
      <div className="cb-bars mt-8 flex items-end justify-center gap-1 h-8" aria-hidden="true">
        {bars.map((i) => <i key={i} style={{ animationDelay: `${(i % 5) * 0.12}s` }} />)}
      </div>
      <p className="mt-5 text-lg font-light tracking-tight" style={{ color: ink.fg }}>Listening…</p>
      <p className="mt-1 text-xs" style={{ color: ink.fg3 }}>Speak now. I&apos;ll send it when you pause.</p>
      <button
        type="button"
        onClick={onStop}
        className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs cursor-pointer transition-colors hover:bg-white/10"
        style={{ borderColor: 'rgba(255,255,255,0.2)', color: ink.fg2 }}
      >
        <MicOff size={13} /> Stop
      </button>
    </div>
  );
}

const stamp = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const makeInitialMessages = () => [
  {
    id: 1,
    text: `Hi! I'm Rakshan's portfolio assistant. Ask me anything about his experience, projects, skills or how to reach him — I answer from his real profile, so you get accurate details while he's away.`,
    isBot: true,
    timestamp: stamp()
  },
  {
    id: 2,
    text: `**Quick info**\n• Senior Software Developer at Foxwel.ai · 2 years\n• Full stack (React, Next.js, Node.js) and GenAI (RAG, LangChain, agents)\n• Email ${contactInfo.email} · Phone ${contactInfo.phone}`,
    links: [{ label: 'LinkedIn', url: `https://${contactInfo.linkedin}` }, { label: 'GitHub', url: `https://${contactInfo.github}` }, { label: 'Download resume (PDF)', url: contactInfo.resume }],
    isBot: true,
    timestamp: stamp()
  }
];

export default function ChatbotSection() {
  const [messages, setMessages] = useState(makeInitialMessages);
  const [inputMessage, setInputMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const reduceMotion = useReducedMotion();
  const contextRef = useRef({ lastIntent: null, seenIntents: [], askedQuestions: [], turn: 0, nudged: false });
  const streamRef = useRef(null);
  const [hasOpened, setHasOpened] = useState(() => {
    try { return sessionStorage.getItem('cb-opened') === '1'; } catch { return false; }
  });

  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const panelRef = useRef(null);
  const handleSendMessageRef = useRef(null);
  useFocusTrap(panelRef, isOpen);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
        setIsListening(false);
        setTimeout(() => {
          if (handleSendMessageRef.current) {
            handleSendMessageRef.current(transcript);
          }
        }, 300);
      };

      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    }

    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (hasOpened || isOpen) return undefined;
    const show = setTimeout(() => setShowHint(true), 2500);
    const hide = setTimeout(() => setShowHint(false), 11000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [hasOpened, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setHasOpened(true);
    setShowHint(false);
    try { sessionStorage.setItem('cb-opened', '1'); } catch { /* storage unavailable */ }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (messagesContainerRef.current && isOpen) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isOpen, isTyping]);

  const stopStream = () => {
    if (streamRef.current) { clearInterval(streamRef.current); streamRef.current = null; }
    setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, text: m.full, streaming: false } : m)));
  };

  const handleSendMessage = (messageText = null) => {
    const text = (messageText || inputMessage).trim();
    if (!text) return;
    stopStream();

    setMessages((prev) => [...prev, { id: Date.now(), text, isBot: false, timestamp: stamp() }]);
    setInputMessage('');
    setIsTyping(true);

    const ctx = contextRef.current;
    const reply = assistant.ask(text, ctx);
    contextRef.current = {
      lastIntent: reply.intent,
      seenIntents: [...new Set([...ctx.seenIntents, reply.intent])],
      askedQuestions: [...ctx.askedQuestions, text.toLowerCase()],
      turn: ctx.turn + 1,
      nudged: Boolean(ctx.nudged || reply.nudged),
    };

    // Brief "thinking" pause scaled to the question, then stream the answer in.
    const thinkFor = reduceMotion ? 0 : Math.min(900, 350 + text.length * 8);
    setTimeout(() => {
      setIsTyping(false);
      const id = Date.now() + 1;
      const full = reply.text;
      const botMessage = { id, text: reduceMotion ? full : '', full, isBot: true, streaming: !reduceMotion, links: reply.links || [], followUps: reply.followUps || [], intent: reply.intent, timestamp: stamp() };
      setMessages((prev) => [...prev, botMessage]);

      if (!reduceMotion) {
        let i = 0;
        streamRef.current = setInterval(() => {
          // Reveal in word-sized steps so it reads like typing, not a ticker.
          const next = full.indexOf(' ', i + 1);
          i = next === -1 ? full.length : Math.min(full.length, next + 1);
          const done = i >= full.length;
          setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: full.slice(0, i), streaming: !done } : m)));
          if (done) { clearInterval(streamRef.current); streamRef.current = null; }
        }, 22);
      }

      if (voiceOutputEnabled && synthRef.current && full.length < 400) {
        const utterance = new SpeechSynthesisUtterance(full.replace(/\*\*/g, '').replace(/[•—]/g, '').replace(/https?:\/\/\S+/g, ''));
        utterance.rate = 0.95;
        synthRef.current.speak(utterance);
      }
    }, thinkFor);
  };
  handleSendMessageRef.current = handleSendMessage;

  const handleVoiceClick = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const handleNewChat = () => {
    stopStream();
    contextRef.current = { lastIntent: null, seenIntents: [], askedQuestions: [], turn: 0, nudged: false };
    if (recognitionRef.current && isListening) recognitionRef.current.stop();
    if (synthRef.current) synthRef.current.cancel();
    setIsListening(false);
    setIsTyping(false);
    setInputMessage('');
    setMessages(makeInitialMessages());
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const hasConversation = messages.some((m) => !m.isBot);

  return (
    <div id="chatbot" className="fixed bottom-6 right-6 z-50">
      {/* Floating Trigger Button */}
      {!isOpen && (
        <div
          className="flex items-center gap-3"
          onMouseEnter={() => setShowHint(true)}
          onMouseLeave={() => setShowHint(false)}
        >
          <button
            type="button"
            onClick={() => { setShowHint(true); setIsOpen(true); }}
            className={`cb-hint-label max-[380px]:hidden px-3.5 py-2 rounded-full text-xs font-medium border shadow-[var(--shadow-modal)] cursor-pointer whitespace-nowrap ${showHint ? 'cb-hint-in' : 'cb-hint-out'}`}
            style={{ background: ink.canvas, color: ink.fg, borderColor: 'rgba(255,255,255,0.18)' }}
            aria-hidden={!showHint}
            tabIndex={showHint ? 0 : -1}
          >
            Ask me about Rakshan
            <span className="ml-1.5" style={{ color: ink.fg3 }}>→</span>
          </button>

          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className={`cb-cta group relative h-12 w-12 rounded-full flex items-center justify-center cursor-pointer shadow-[var(--shadow-modal)] transition-transform hover:scale-105 border ${hasOpened ? '' : 'cb-cta-attention'}`}
            style={{ background: ink.canvas, color: ink.fg, borderColor: 'rgba(255,255,255,0.18)' }}
            aria-label="Open Portfolio Assistant"
            title="Open Assistant"
          >
            {!hasOpened && (
              <>
                <span className="cb-cta-ring absolute inset-0 rounded-full" aria-hidden="true" />
                <span className="cb-cta-ring cb-cta-ring-2 absolute inset-0 rounded-full" aria-hidden="true" />
              </>
            )}
            <span className="cb-cta-orb flex items-center justify-center">
              <OrbMark size={22} />
            </span>
          </button>
        </div>
      )}

      {/* Floating Chat Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="cb-shell flex flex-col overflow-hidden shadow-[var(--shadow-modal)] border max-sm:fixed max-sm:inset-0 max-sm:w-auto max-sm:h-auto max-sm:rounded-none max-sm:border-0 sm:w-[400px] sm:h-[640px] sm:max-h-[calc(100vh-3rem)] sm:rounded-[28px]"
          style={{ background: ink.canvas, color: ink.fg, borderColor: 'rgba(255,255,255,0.12)' }}
          role="dialog"
          aria-label="Portfolio Assistant Chat"
        >
          {/* Header */}
          <div className="relative flex items-center justify-between px-4 pt-4 pb-3 max-sm:pt-[max(1rem,env(safe-area-inset-top))]">
            <div className="flex items-center">
            <button
              type="button"
              onClick={handleNewChat}
              disabled={!hasConversation}
              className="p-2 rounded-full transition-colors cursor-pointer hover:bg-white/10 disabled:opacity-30 disabled:cursor-default"
              style={{ color: ink.fg2 }}
              title="New chat"
              aria-label="Start a new chat"
            >
              <SquarePen size={16} />
            </button>
            <button
              type="button"
              onClick={() => setVoiceOutputEnabled(!voiceOutputEnabled)}
              className="p-2 rounded-full transition-colors cursor-pointer hover:bg-white/10"
              style={{ color: voiceOutputEnabled ? ink.fg : ink.fg3 }}
              title={voiceOutputEnabled ? 'Mute voice' : 'Enable voice'}
            >
              {voiceOutputEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            </div>

            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[11px] font-medium tracking-[0.22em] uppercase" style={{ color: ink.fg2 }}>
              <OrbMark size={14} />
              <span>Rakshan</span>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full transition-colors cursor-pointer hover:bg-white/10"
              style={{ color: ink.fg3 }}
              aria-label="Close chat"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="relative flex-1 min-h-0 flex flex-col">
          {isListening && <ListeningOverlay onStop={handleVoiceClick} />}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto scroll-smooth [scrollbar-width:thin] [scrollbar-color:#2e2e2e_transparent]">
            {!hasConversation ? (
              <div className="px-6 pt-4 pb-6 flex flex-col min-h-full">
                <WireOrb />
                <h2 className="cb-fade-text mt-6 text-[26px] leading-[1.15] tracking-tight">
                  <span className="font-semibold" style={{ color: ink.fg }}>I can walk you through</span>{' '}
                  <span className="font-light" style={{ color: ink.fg }}>Rakshan&apos;s experience, projects, stack, and</span>{' '}
                  <span className="font-light" style={{ color: ink.fg2 }}>how to reach him.</span>
                </h2>
                <p className="mt-3 text-xs" style={{ color: ink.fg3 }}>
                  Ask anything, or start with one of these.
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {quickQuestions.slice(0, 4).map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => handleSendMessage(q)}
                      className="text-[11px] px-3 py-1.5 rounded-full border transition-colors cursor-pointer hover:bg-white/10"
                      style={{ borderColor: ink.line, color: ink.fg2 }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="px-4 py-3 space-y-5">
                {messages.map((msg, idx) =>
                  msg.isBot ? (
                    <BotMessageBubble key={msg.id} message={msg} isLast={idx === messages.length - 1} onFollowUp={handleSendMessage} />
                  ) : (
                    <div key={msg.id} className="flex justify-end pl-10">
                      <div
                        className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-[13px] leading-relaxed whitespace-pre-wrap"
                        style={{ background: ink.bubble, color: ink.fg }}
                      >
                        {msg.text}
                      </div>
                    </div>
                  )
                )}

                {isTyping && (
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center border" style={{ borderColor: 'rgba(255,255,255,0.25)', color: ink.fg }}>
                      <OrbMark size={13} />
                    </span>
                    <span className="cb-dots flex items-center gap-1" aria-label="Assistant is typing">
                      <i /><i /><i />
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          </div>

          {/* Quick questions (compact row once a conversation has started) */}
          {hasConversation && (
            <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto [scrollbar-width:none]">
              {quickQuestions.slice(0, 4).map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => handleSendMessage(q)}
                  className="shrink-0 text-[11px] px-3 py-1.5 rounded-full border transition-colors cursor-pointer hover:bg-white/10"
                  style={{ borderColor: ink.line, color: ink.fg2 }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <div className="px-4 pb-4 pt-2 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center gap-2">
              <div
                className="flex-1 flex items-center rounded-full border pl-4 pr-1.5 py-1.5 focus-within:border-white/30 transition-colors"
                style={{ background: ink.panel, borderColor: 'rgba(255,255,255,0.14)' }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Send a message..."
                  className="flex-1 min-w-0 bg-transparent text-[13px] focus:outline-none"
                  style={{ color: ink.fg }}
                />
                <button
                  type="button"
                  onClick={handleVoiceClick}
                  className={`p-2 rounded-full transition-colors cursor-pointer hover:bg-white/10 ${isListening ? 'animate-pulse' : ''}`}
                  style={{ color: isListening ? ink.fg : ink.fg3 }}
                  title="Voice input"
                  aria-label={isListening ? 'Stop listening' : 'Start voice input'}
                >
                  {isListening ? <MicOff size={15} /> : <Mic size={15} />}
                </button>
              </div>

              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim()}
                className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center cursor-pointer transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: ink.fg, color: ink.canvas }}
                aria-label="Send message"
              >
                <ArrowUp size={16} strokeWidth={2.2} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
